import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import {
  Bell, BellRing, Sun, Moon, Star, Cloud, Clock,
  Plus, Trash2, Check, X, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '../context/I18nContext';

// Store alerts in localStorage
const ALERTS_KEY = 'luminaspot_alerts';

const SmartAlerts = ({ latitude, longitude, shotScore, sunTimes, moonData }) => {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    type: 'golden_hour',
    enabled: true,
    threshold: 70,
    beforeMinutes: 30
  });

  // Load alerts from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(ALERTS_KEY);
    if (saved) {
      try {
        setAlerts(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load alerts');
      }
    }
  }, []);

  // Save alerts to localStorage
  useEffect(() => {
    localStorage.setItem(ALERTS_KEY, JSON.stringify(alerts));
  }, [alerts]);

  // Check alerts and trigger notifications
  useEffect(() => {
    if (!('Notification' in window)) return;

    const checkAlerts = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      alerts.forEach(alert => {
        if (!alert.enabled) return;

        let shouldNotify = false;
        let message = '';

        switch (alert.type) {
          case 'golden_hour':
            // Parse golden hour time
            const goldenStart = sunTimes?.golden_hour_evening?.start;
            if (goldenStart) {
              const [gh, gm] = goldenStart.split(':').map(Number);
              const alertTime = gh * 60 + gm - alert.beforeMinutes;
              const currentTime = currentHour * 60 + currentMinute;
              if (Math.abs(currentTime - alertTime) < 1) {
                shouldNotify = true;
                message = `🌅 ${t('alerts.goldenHour')} ${t('alerts.startsIn')} ${alert.beforeMinutes} ${t('alerts.minutesLabel')}!`;
              }
            }
            break;

          case 'shot_score':
            if (shotScore?.shot_score?.score >= alert.threshold) {
              // Only notify once per hour
              const lastNotified = localStorage.getItem(`alert_${alert.id}_last`);
              const lastHour = lastNotified ? parseInt(lastNotified) : -1;
              if (currentHour !== lastHour) {
                shouldNotify = true;
                message = `⚡ ${t('alerts.shotScoreIs')} ${shotScore.shot_score.score}% - ${t('alerts.greatConditions')}`;
                localStorage.setItem(`alert_${alert.id}_last`, currentHour.toString());
              }
            }
            break;

          case 'blue_hour':
            const blueStart = sunTimes?.blue_hour_evening?.start;
            if (blueStart) {
              const [bh, bm] = blueStart.split(':').map(Number);
              const alertTime = bh * 60 + bm - alert.beforeMinutes;
              const currentTime = currentHour * 60 + currentMinute;
              if (Math.abs(currentTime - alertTime) < 1) {
                shouldNotify = true;
                message = `🌃 ${t('alerts.blueHour')} ${t('alerts.startsIn')} ${alert.beforeMinutes} ${t('alerts.minutesLabel')}!`;
              }
            }
            break;

          case 'new_moon':
            if (moonData?.phase_name === 'New Moon') {
              const lastNotified = localStorage.getItem(`alert_${alert.id}_date`);
              const today = now.toDateString();
              if (lastNotified !== today) {
                shouldNotify = true;
                message = `🌑 ${t('alerts.newMoonTonight')} - ${t('alerts.perfectForAstro')}`;
                localStorage.setItem(`alert_${alert.id}_date`, today);
              }
            }
            break;
        }

        if (shouldNotify && Notification.permission === 'granted') {
          new Notification('LuminaSpot Alert', { body: message, icon: '/favicon.ico' });
          toast.success(message);
        }
      });
    };

    const interval = setInterval(checkAlerts, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [alerts, shotScore, sunTimes, moonData, t]);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error(t('alerts.notificationsNotSupported'));
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      toast.success(t('alerts.notificationsEnabledMsg'));
    } else {
      toast.error(t('alerts.notificationPermissionDenied'));
    }
  };

  const addAlert = () => {
    const alert = {
      ...newAlert,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    setAlerts([...alerts, alert]);
    setShowAddAlert(false);
    setNewAlert({ type: 'golden_hour', enabled: true, threshold: 70, beforeMinutes: 30 });
    toast.success(t('alerts.alertCreated'));
  };

  const toggleAlert = (id) => {
    setAlerts(alerts.map(a =>
      a.id === id ? { ...a, enabled: !a.enabled } : a
    ));
  };

  const deleteAlert = (id) => {
    setAlerts(alerts.filter(a => a.id !== id));
    toast.success(t('alerts.alertDeleted'));
  };

  const alertTypes = [
    { value: 'golden_hour', label: t('alerts.goldenHour'), icon: Sun, color: 'text-amber-500' },
    { value: 'blue_hour', label: t('alerts.blueHour'), icon: Moon, color: 'text-blue-500' },
    { value: 'shot_score', label: t('alerts.shotScore'), icon: Star, color: 'text-emerald-500' },
    { value: 'new_moon', label: t('alerts.newMoon'), icon: Moon, color: 'text-purple-500' },
  ];

  const getAlertIcon = (type) => {
    const alertType = alertTypes.find(t => t.value === type);
    return alertType ? alertType.icon : Bell;
  };

  const getAlertColor = (type) => {
    const alertType = alertTypes.find(t => t.value === type);
    return alertType ? alertType.color : 'text-slate-400';
  };

  const getAlertLabel = (type) => {
    const alertType = alertTypes.find(t => t.value === type);
    return alertType ? alertType.label : type;
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <BellRing className="w-5 h-5 text-amber-500" />
              {t('alerts.title')}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={requestNotificationPermission}
              className="border-white/10 text-xs"
              data-testid="enable-notifications-btn"
            >
              <Bell className="w-3 h-3 mr-1" />
              {t('alerts.enableNotifications')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Current Status */}
          <div className="grid grid-cols-2 gap-2 mb-4 p-3 rounded-lg bg-slate-800/50">
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-slate-400">{t('alerts.goldenHour')}:</span>
              <span className="text-xs font-mono text-white">
                {sunTimes?.golden_hour_evening?.start || '--:--'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-slate-400">{t('shotScore.title')}:</span>
              <span className="text-xs font-mono text-white">
                {shotScore?.shot_score?.score || '--'}%
              </span>
            </div>
          </div>

          {/* Alerts List */}
          <div className="space-y-2 mb-4">
            {alerts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('alerts.noAlertsConfigured')}</p>
                <p className="text-xs">{t('alerts.noAlertsHint')}</p>
              </div>
            ) : (
              alerts.map(alert => {
                const Icon = getAlertIcon(alert.type);
                return (
                  <div
                    key={alert.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      alert.enabled 
                        ? 'bg-slate-800/50 border-white/10' 
                        : 'bg-slate-900/30 border-white/5 opacity-50'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${getAlertColor(alert.type)}`} />
                      <div>
                        <p className="text-sm text-white">{getAlertLabel(alert.type)}</p>
                        <p className="text-xs text-slate-500">
                          {alert.type === 'shot_score'
                            ? `${t('alerts.whenScoreAbove')} ${alert.threshold}%`
                            : `${alert.beforeMinutes} ${t('alerts.minBefore')}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={alert.enabled}
                        onCheckedChange={() => toggleAlert(alert.id)}
                        data-testid={`alert-toggle-${alert.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteAlert(alert.id)}
                        className="text-slate-500 hover:text-red-500"
                        data-testid={`alert-delete-${alert.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Add Alert Form */}
          {showAddAlert ? (
            <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10 space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">{t('alerts.alertType')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {alertTypes.map(type => (
                    <Button
                      key={type.value}
                      variant={newAlert.type === type.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewAlert({ ...newAlert, type: type.value })}
                      className={newAlert.type === type.value 
                        ? 'bg-amber-500 text-black' 
                        : 'border-white/10'
                      }
                    >
                      <type.icon className={`w-4 h-4 mr-1 ${type.color}`} />
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              {newAlert.type === 'shot_score' ? (
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('alerts.scoreThreshold')}: {newAlert.threshold}%</Label>
                  <Slider
                    value={[newAlert.threshold]}
                    onValueChange={([v]) => setNewAlert({ ...newAlert, threshold: v })}
                    min={50}
                    max={95}
                    step={5}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('alerts.notifyBefore')}: {newAlert.beforeMinutes} min</Label>
                  <Slider
                    value={[newAlert.beforeMinutes]}
                    onValueChange={([v]) => setNewAlert({ ...newAlert, beforeMinutes: v })}
                    min={5}
                    max={60}
                    step={5}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={addAlert}
                  className="flex-1 bg-amber-500 text-black hover:bg-amber-400"
                  data-testid="save-alert-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {t('alerts.addAlert')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddAlert(false)}
                  className="border-white/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => setShowAddAlert(true)}
              className="w-full bg-slate-800 hover:bg-slate-700 border border-white/10"
              data-testid="add-alert-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('alerts.addAlert')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartAlerts;
